import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Typography,
  Avatar,
  Paper,
  useTheme,
  alpha,
  Tooltip,
  Badge,
  IconButton,
  styled,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as DocumentIcon,
  Group as UsersIcon,
  SupervisorAccount as RolesIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as SuperAdminIcon,
  Domain as DepartmentIcon,
  Assessment as ReportsIcon,
  ExpandLess,
  ExpandMore,
  Add as AddIcon,
  List as ListIcon,
  Task as TaskIcon,
  History as HistoryIcon,
  LocationCity as MinistryIcon,
  Map as ProvinceIcon,
  Logout as LogoutIcon,
  AccountCircle as ProfileIcon,
  NotificationsNone as NotificationIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import moicLogo from '../../assets/images/moiclogo.png';

// Modern styled components with enhanced visual elements
const NAV_WIDTH = 260;
const COLLAPSED_WIDTH = 72;

const SidebarContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'collapsed' && prop !== 'isRtl' })(({ theme, collapsed }) => ({
  width: collapsed ? COLLAPSED_WIDTH : NAV_WIDTH,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  overflow: 'hidden',
  transition: 'all 0.25s ease',
  position: 'relative',
  zIndex: 5,
  ...(collapsed && {
    '& .MuiListItemText-root': {
      display: 'none'
    },
    '& .MuiListItemButton-root': {
      justifyContent: 'center',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1)
    },
    '& .MuiListItemIcon-root': {
      minWidth: theme.spacing(4.5),
      justifyContent: 'center'
    }
  })
}));

const LogoContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'isRtl' && prop !== 'collapsed' })(({ theme, isRtl, collapsed }) => ({
  padding: theme.spacing(2),
  minHeight: theme.mixins.toolbar.minHeight,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.common.white,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
  ...(isRtl ? {} : {}),
  '& .logo-text': {
    display: collapsed ? 'none' : 'block'
  }
}));

const ProfileContainer = styled(Paper, { shouldForwardProp: (prop) => prop !== 'isRtl' && prop !== 'collapsed' })(({ theme, isRtl, collapsed }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: 16,
  background: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  '&:hover': {
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
}));

const StyledNavItem = styled(ListItemButton, { shouldForwardProp: (prop) => prop !== 'collapsed' && prop !== 'active' && prop !== 'isRtl' })(({ theme, active, isRtl, collapsed }) => ({
  borderRadius: 12,
  marginBottom: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(1, 2),
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
  border: `1px solid ${active ? alpha(theme.palette.primary.main, 0.2) : 'transparent'}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': active ? {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '3px',
    background: theme.palette.primary.main,
    borderRadius: '0 2px 2px 0',
  } : {},
  '&:hover': {
    backgroundColor: active ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.main, 0.04),
    transform: 'translateX(4px)',
    borderColor: active ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.3),
  },
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '& .MuiListItemIcon-root': {
    color: active ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.7),
    minWidth: theme.spacing(4),
    transition: 'all 0.3s ease',
  },
  '& .MuiListItemText-primary': {
    fontWeight: active ? 600 : 500,
    fontSize: '0.9rem',
    transition: 'all 0.3s ease',
    textAlign: isRtl ? 'right' : 'left',
    display: collapsed ? 'none' : 'block',
  }
}));

const SubNavItem = styled(StyledNavItem)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(2),
  borderRadius: 8,
  marginBottom: theme.spacing(0.5),
}));

const RtlSubNavItem = styled(StyledNavItem)(({ theme }) => ({
  paddingRight: theme.spacing(4),
  paddingLeft: theme.spacing(2),
  borderRadius: 8,
  marginBottom: theme.spacing(0.5),
}));

const MenuHeader = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(3),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  boxShadow: `0 2px 10px ${alpha(theme.palette.common.black, 0.15)}`,
  border: `3px solid ${theme.palette.background.paper}`,
  backgroundColor: theme.palette.primary.main,
  transition: 'all 0.3s ease',
}));

const UserInfo = styled(Box, { shouldForwardProp: (prop) => prop !== 'collapsed' && prop !== 'isRtl' })(({ theme, isRtl, collapsed }) => ({
  marginLeft: isRtl ? 0 : theme.spacing(2),
  marginRight: isRtl ? theme.spacing(2) : 0,
  overflow: 'hidden',
  display: collapsed ? 'none' : 'block'
}));

const RoleChip = styled(Chip)(({ theme }) => ({
  height: 22,
  fontSize: '0.7rem',
  fontWeight: 600,
  marginTop: 0,
  background: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.dark,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  '& .MuiChip-label': {
    padding: '0 8px',
  }
}));

const StatusBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    backgroundColor: theme.palette.success.main,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: `1px solid ${theme.palette.success.main}`,
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const VersionFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  textAlign: 'center',
  borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.6)} 0%, ${alpha(theme.palette.primary.light, 0.06)} 100%)`,
  backdropFilter: 'blur(6px)',
}));

const Sidebar = () => {
  const { t, i18n } = useTranslation(['app', 'documents', 'common', 'navigation', 'superadmin', 'limitedadmin']);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';
  const theme = useTheme();
  
  // State for tracking expanded menu items
  const [openMenus, setOpenMenus] = useState({
    users: location.pathname.startsWith('/users') || location.pathname.startsWith('/roles'),
    administration: location.pathname.includes('ministries') || 
                   location.pathname.includes('departments') || 
                   location.pathname.includes('directorates') || 
                   location.pathname.includes('provinces')
  });
  
  // collapsed sidebar state
  const [collapsed, setCollapsed] = useState(false);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      window.dispatchEvent(new CustomEvent('sidebar-toggle', { detail: next }));
      return next;
    });
  };
  
  // Toggle submenu
  const handleToggleMenu = (menu) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };
  
  // Check if a path is currently active
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  // Check if a path is partially active (for parent menus)
  const isPartiallyActive = (path) => {
    return location.pathname.startsWith(path);
  };
  
  // Get initial letter for avatar
  const getAvatarInitial = () => {
    // Use the username field when available, otherwise fall back to email
    if (user?.username) return user.username.charAt(0).toUpperCase();
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return 'U';
  };

  // Get display name - prioritize actual username field
  const getDisplayName = () => {
    // Use the actual username field when it exists
    if (user?.username) return user.username;
    
    // Fall back to email if no username
    if (user?.email) {
      return user.email.split('@')[0];
    }
    
    return t('common:user');
  };

  // Determine if the logged-in user is a super admin
  const isSuperAdmin = (
    (user?.role?.name && user.role.name.toLowerCase().includes('super')) ||
    (Array.isArray(user?.roles) && user.roles.some(r => r.toLowerCase().includes('super'))) ||
    (user?.role?.Permissions && user.role.Permissions.some(p => p.code === 'super_admin_access')) ||
    (user?.permissions && user.permissions.includes('super_admin_access')) ||
    (user?.email === 'admin@gmail.com') // Fallback for testing
  );

  // Determine if the logged-in user is a limited admin
  const isLimitedAdmin = (
    (user?.role?.name && user.role.name === 'Limited Admin') ||
    (user?.role?.Permissions && user.role.Permissions.some(p => p.code === 'limited_admin_access'))
  );
  
  return (
    <SidebarContainer dir={isRtl ? 'rtl' : 'ltr'} collapsed={collapsed}>
      {/* Logo and title header */}
      <LogoContainer isRtl={isRtl} collapsed={collapsed}>
        {/* Main title */}
        <Typography
          variant="h6"
          component="div"
          align="center"
          sx={{
            fontWeight: 800,
            fontSize: '1.25rem',
            lineHeight: 1.2,
            textAlign: 'center',
            letterSpacing: '0.01em',
            color: theme.palette.common.white,
            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
            mb: 0.5,
          }}
          className="logo-text"
        >
          {t('app:title', 'Document Tracking System')}
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="subtitle2"
          component="div"
          align="center"
          sx={{
            fontWeight: 400,
            fontSize: '0.8rem',
            color: alpha(theme.palette.common.white, 0.8),
            mt: 0,
          }}
          className="logo-text"
        >
          {t('app:subtitle', 'د اسنادو دتعقیب سیستم')}
        </Typography>

        {/* Collapse/Expand Button */}
        <IconButton
          onClick={toggleCollapsed}
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            [isRtl ? 'left' : 'right']: 8,
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
            },
            width: 32,
            height: 32,
          }}
        >
          {collapsed ? 
            (isRtl ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />) :
            (isRtl ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />)
          }
        </IconButton>
      </LogoContainer>
      
      {/* User profile section - modernized */}
      {user && (
        <ProfileContainer isRtl={isRtl} collapsed={collapsed} elevation={0}>
          <StatusBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
            variant="dot"
          >
            <UserAvatar>
              {getAvatarInitial()}
            </UserAvatar>
          </StatusBadge>
          <UserInfo isRtl={isRtl} collapsed={collapsed}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.text.primary,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                maxWidth: 170,
              }}
            >
              {getDisplayName()}
            </Typography>
            <RoleChip 
              label={user.role?.name || user.position || t('common:user')}
              size="small"
            />
          </UserInfo>
        </ProfileContainer>
      )}
      
      {/* Navigation menu - enhanced visual styling */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1,
          pb: 1,
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
            borderRadius: '4px',
          },
        }}
      >
       
        
        <List disablePadding>
        {/* Dashboard */}
        <ListItem disablePadding>
            <StyledNavItem
            onClick={() => navigate('/dashboard')}
              active={isActive('/dashboard')}
            isRtl={isRtl}
          >
              <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
              <ListItemText primary={t('app:dashboard')} />
            </StyledNavItem>
        </ListItem>

        {/* Super Admin Dashboard */}
        {isSuperAdmin && (
          <ListItem disablePadding>
            <StyledNavItem
              onClick={() => navigate('/super-admin')}
              active={isActive('/super-admin')}
              isRtl={isRtl}
            >
              <ListItemIcon>
                <SuperAdminIcon />
              </ListItemIcon>
              <ListItemText primary={t('superadmin:super_admin_dashboard')} />
            </StyledNavItem>
          </ListItem>
        )}

        {/* Limited Admin Dashboard */}
        {isLimitedAdmin && (
          <ListItem disablePadding>
            <StyledNavItem
              onClick={() => navigate('/limited-admin')}
              active={isActive('/limited-admin')}
              isRtl={isRtl}
            >
              <ListItemIcon>
                <SuperAdminIcon />
              </ListItemIcon>
              <ListItemText primary={t('limitedadmin:limited_admin_dashboard')} />
            </StyledNavItem>
          </ListItem>
        )}
        
        <Divider sx={{ my: 0, mx: 1 }} />
        <MenuHeader sx={{ textAlign: isRtl ? 'right' : 'left' }}>
          {t('navigation:documents', 'Documents')}
        </MenuHeader>
        
        {/* All Documents */}
        <ListItem disablePadding>
          <StyledNavItem
            onClick={() => navigate('/documents')}
            active={isActive('/documents')}
            isRtl={isRtl}
          >
            <ListItemIcon>
              <DocumentIcon />
            </ListItemIcon>
            <ListItemText primary={t('documents:allDocuments')} />
          </StyledNavItem>
        </ListItem>

        {/* Create Document */}
        <ListItem disablePadding>
          <StyledNavItem
            onClick={() => navigate('/documents/create')}
            active={isActive('/documents/create')}
            isRtl={isRtl}
          >
            <ListItemIcon>
              <AddIcon />
            </ListItemIcon>
            <ListItemText primary={t('documents:createDocument')} />
          </StyledNavItem>
        </ListItem>

        <Divider sx={{ my: 0, mx: 1 }} />
        <MenuHeader sx={{ textAlign: isRtl ? 'right' : 'left' }}>
          {t('navigation:tasks', 'Tasks')}
        </MenuHeader>

        {/* Tasks Assigned To Me */}
        <ListItem disablePadding>
          <StyledNavItem
            onClick={() => navigate('/tasks')}
            active={isActive('/tasks')}
            isRtl={isRtl}
          >
            <ListItemIcon>
              <TaskIcon />
            </ListItemIcon>
            <ListItemText primary={t('tasks:assignedtome')} />
          </StyledNavItem>
        </ListItem>

        {/* Tasks Assigned By Me */}
        <ListItem disablePadding>
          <StyledNavItem
            onClick={() => navigate('/tasks/assigned-by-me')}
            active={isActive('/tasks/assigned-by-me') && !location.search.includes('assign=1')}
            isRtl={isRtl}
          >
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary={t('tasks:assignedByMe', 'Assigned By Me')} />
          </StyledNavItem>
        </ListItem>

        {/* Assign Task */}
        <ListItem disablePadding>
          <StyledNavItem
            onClick={() => navigate('/tasks/assigned-by-me?assign=1')}
            active={location.pathname === '/tasks/assigned-by-me' && location.search.includes('assign=1')}
            isRtl={isRtl}
          >
            <ListItemIcon>
              <AddIcon />
            </ListItemIcon>
            <ListItemText primary={t('assigned_by_me:assign_new_task', 'Assign Task')} />
          </StyledNavItem>
        </ListItem>
        </List>

        <Divider sx={{ my: 0, mx: 1 }} />
        
        {isSuperAdmin && (
          <>
            <MenuHeader sx={{ textAlign: isRtl ? 'right' : 'left' }}>
              {t('navigation:management', 'Management')}
            </MenuHeader>
            <List disablePadding>
              {/* Users */}
              <ListItem disablePadding>
                <StyledNavItem
                  onClick={() => handleToggleMenu('users')}
                  active={isPartiallyActive('/users') || isPartiallyActive('/roles')}
                  isRtl={isRtl}
                >
                  <ListItemIcon>
                    <UsersIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation:users')} />
                  {openMenus.users ? 
                    <ExpandLess color="inherit" fontSize="small" /> : 
                    <ExpandMore color="inherit" fontSize="small" />
                  }
                </StyledNavItem>
              </ListItem>
              
              <Collapse in={openMenus.users} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {isRtl ? (
                    <>
                      <ListItem disablePadding>
                        <RtlSubNavItem
                          onClick={() => navigate('/users')}
                          active={isActive('/users')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <ListIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:users')} />
                        </RtlSubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <RtlSubNavItem
                          onClick={() => navigate('/users/create')}
                          active={isActive('/users/create')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <AddIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:createUser')} />
                        </RtlSubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <RtlSubNavItem
                          onClick={() => navigate('/roles')}
                          active={isActive('/roles')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <RolesIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:roles')} />
                        </RtlSubNavItem>
                      </ListItem>
                    </>
                  ) : (
                    <>
                      <ListItem disablePadding>
                        <SubNavItem
                          onClick={() => navigate('/users')}
                          active={isActive('/users')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <ListIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:users')} />
                        </SubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <SubNavItem
                          onClick={() => navigate('/users/create')}
                          active={isActive('/users/create')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <AddIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:createUser')} />
                        </SubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <SubNavItem
                          onClick={() => navigate('/roles')}
                          active={isActive('/roles')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <RolesIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:roles')} />
                        </SubNavItem>
                      </ListItem>
                    </>
                  )}
                </List>
              </Collapse>
            
            {/* Administration */}
            <ListItem disablePadding>
                <StyledNavItem
                onClick={() => handleToggleMenu('administration')}
                  active={
                  isPartiallyActive('/departments') || 
                  isPartiallyActive('/directorates') || 
                  isPartiallyActive('/deputy-ministries') || 
                  isPartiallyActive('/provinces') ||
                  isPartiallyActive('/ministries')
                }
                isRtl={isRtl}
              >
                  <ListItemIcon>
                  <DepartmentIcon />
                </ListItemIcon>
                  <ListItemText primary={t('navigation:administration')} />
                {openMenus.administration ? 
                    <ExpandLess color="inherit" fontSize="small" /> : 
                    <ExpandMore color="inherit" fontSize="small" />
                }
                </StyledNavItem>
            </ListItem>
              
            <Collapse in={openMenus.administration} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {isRtl ? (
                    <>
                      <ListItem disablePadding>
                        <RtlSubNavItem
                          onClick={() => navigate('/ministries')}
                          active={isPartiallyActive('/ministries')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <MinistryIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:ministries')} />
                        </RtlSubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <RtlSubNavItem
                          onClick={() => navigate('/deputy-ministries')}
                          active={isPartiallyActive('/deputy-ministries')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <MinistryIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:deputy-ministries')} />
                        </RtlSubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <RtlSubNavItem
                          onClick={() => navigate('/directorates')}
                          active={isPartiallyActive('/directorates')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <DepartmentIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:directorates')} />
                        </RtlSubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <RtlSubNavItem
                          onClick={() => navigate('/departments')}
                          active={isPartiallyActive('/departments')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <DepartmentIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:departments')} />
                        </RtlSubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <RtlSubNavItem
                          onClick={() => navigate('/provinces')}
                          active={isPartiallyActive('/provinces')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <ProvinceIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:provinces')} />
                        </RtlSubNavItem>
                      </ListItem>
                    </>
                  ) : (
                    <>
                      <ListItem disablePadding>
                        <SubNavItem
                          onClick={() => navigate('/ministries')}
                          active={isPartiallyActive('/ministries')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <MinistryIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:ministries')} />
                        </SubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <SubNavItem
                          onClick={() => navigate('/deputy-ministries')}
                          active={isPartiallyActive('/deputy-ministries')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <MinistryIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:deputy-ministries')} />
                        </SubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <SubNavItem
                          onClick={() => navigate('/directorates')}
                          active={isPartiallyActive('/directorates')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <DepartmentIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:directorates')} />
                        </SubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <SubNavItem
                          onClick={() => navigate('/departments')}
                          active={isPartiallyActive('/departments')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <DepartmentIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:departments')} />
                        </SubNavItem>
                      </ListItem>
                      <ListItem disablePadding>
                        <SubNavItem
                          onClick={() => navigate('/provinces')}
                          active={isPartiallyActive('/provinces')}
                          isRtl={isRtl}
                        >
                          <ListItemIcon>
                            <ProvinceIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navigation:provinces')} />
                        </SubNavItem>
                      </ListItem>
                    </>
                  )}
                </List>
              </Collapse>
            </List>
          </>
        )}
        
        {/* Other links */}
        <List disablePadding>
        {/* Reports */}
        <ListItem disablePadding>
            <StyledNavItem
            onClick={() => navigate('/reports')}
              active={isPartiallyActive('/reports')}
            isRtl={isRtl}
          >
              <ListItemIcon>
              <ReportsIcon />
            </ListItemIcon>
              <ListItemText primary={t('navigation:reports')} />
            </StyledNavItem>
        </ListItem>
        
        {/* Settings */}
        <ListItem disablePadding>
            <StyledNavItem
            onClick={() => navigate('/settings')}
              active={isActive('/settings')}
            isRtl={isRtl}
          >
              <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
              <ListItemText primary={t('app:settings')} />
            </StyledNavItem>
        </ListItem>
        </List>
      </Box>
      
    
    </SidebarContainer>
  );
};

export default Sidebar;